import { useEffect, useRef, useState } from 'react';
import { NoticeBody } from '../components/NoticeBody';

/**
 * PC 端投诉须知闸 —— 2026-09-07 主公：「PC 端做一个投诉须知，同意后才显示表单」。
 *
 * ════════ 与手机端的关系 ════════
 *
 * 正文**共用** `components/NoticeBody.tsx`（协会正式文本，一个字都不许有第二份）。
 * 外壳与交互各写各的，因为两端形态差别大：
 *   · 手机端是**独立一屏**（`/notice` 路由），同意后 `nav('/submit')` 跳过去；
 *   · PC 端只有一个页面（`pc.html`，协会官网挂链接进来，没有路由），
 *     所以做成**同一张卡片内的两态**：先须知，同意后就地换成表单。
 *
 * ★ 为什么 PC 不新开一个路由：这一页是**独立打开**的单页（见 `PcApp.tsx` 文件头），
 *   没有 Router。为一个闸引一套路由，会让「从协会官网点进来」这条唯一入口
 *   多出一个可以被直接绕过的地址（有人把 `#/submit` 发给别人，闸就没了）。
 *   两态放在同一个组件里，绕不过去。
 *
 * ════════ 「同意」这件事怎么算数 ════════
 *
 * ★ 勾选框**默认不勾**，且不做「滚到底自动勾」。自动勾等于替人做了确认，
 *   而这一步的全部意义就是「他自己确认过」。
 * ★ 同意状态**不落 localStorage**：下次打开仍要再看一遍。
 *   须知是协会可以随时修订的文本（正文第一节第（二）条写着「以投诉时页面展示为准」），
 *   记住上次的选择就等于让人对着一份可能已经变了的文本继续有效地「同意过」。
 * ★ 没勾时按钮是禁用态而不是隐藏：让人看得见下一步在哪、缺什么，
 *   而不是对着一个「什么都没有」的页面猜。
 */
export default function PcNotice({ onAgree }: { onAgree: () => void }) {
  const [agreed, setAgreed] = useState(false);
  /** 读到底没有 —— 只用来提示，**不**用它代替勾选（见文件头） */
  const [readEnd, setReadEnd] = useState(false);
  const boxRef = useRef<HTMLDivElement | null>(null);

  /* 正文很长，PC 上放在一个自带滚动的框里；滚到底给一句「已读完」的反馈，
     否则用户不知道自己是不是漏了下半段。 */
  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const onScroll = () => {
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 24) setReadEnd(true);
    };
    onScroll(); // 窗口很高时正文可能一屏就装下，这时一开始就算读完
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <section className="pcnotice" aria-labelledby="pcnotice-t">
      {/* 抬头沿用表单那一套 `.pchero`：同一张页面里两屏的标题不该长得不一样。 */}
      <div className="pchero">
        <h1 id="pcnotice-t">投诉须知</h1>
        <p>请在发起投诉前仔细阅读以下内容，勾选同意后方可填写投诉表单。</p>
      </div>

      <div className="pcsheet pcnotice-sheet">
        <p className="pcnotice-org">成都市房地产经纪协会</p>
        <div
          className="pcnotice-body"
          ref={boxRef}
          tabIndex={0}
          role="region"
          aria-label="投诉须知正文"
        >
          <NoticeBody />
        </div>
      </div>

      <div className="pcnotice-foot">
        <div
          className="pcnotice-agree"
          role="checkbox"
          aria-checked={agreed}
          tabIndex={0}
          onClick={() => setAgreed((v) => !v)}
          onKeyDown={(e) => {
            if (e.key === ' ' || e.key === 'Enter') {
              e.preventDefault();
              setAgreed((v) => !v);
            }
          }}
        >
          <span className={agreed ? 'pcnotice-cbx on' : 'pcnotice-cbx'} aria-hidden="true" />
          <span className="pcnotice-atext">我已阅读并同意《投诉须知》</span>
        </div>

        <button
          type="button"
          className="pcbtn pcbtn-main"
          disabled={!agreed}
          onClick={onAgree}
        >
          我已阅读，开始投诉
        </button>

        {/* 没勾时说清楚缺什么；勾了就不必再说。
            「还没读完」只是提示，不挡按钮 —— 挡了就成了替人判断他读没读。 */}
        {!agreed ? (
          <p className="pcnotice-hint">
            {readEnd ? '请勾选上方「我已阅读并同意」后继续。' : '请阅读全文并勾选上方「我已阅读并同意」后继续。'}
          </p>
        ) : null}
      </div>
    </section>
  );
}
